'use client';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Layers, Package, Power, PowerOff, Search, Trash2, User, type LucideIcon } from 'lucide-react';
import { Avatar } from '../../components/ui/Avatar';
import { LabelMark } from '../../components/ui/StatusMark';
import { Button } from '../../components/ui/Button';
import { TitleLink } from '../../components/ui/TitleLink';
import { IconTile } from '../../components/ui/IconTile';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/shadcn/textarea';
import { Pager } from '../../components/ui/Pager';
import { paginateItems } from '../../lib/paginateItems';
import { RegisterSearch } from '../../components/ui/RegisterSearch';
import { Field } from '../../components/ui/Field';
import { SelectMenu } from '../../components/ui/SelectMenu';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { CardGrid, CardGridItem } from '../../components/ui/CardGrid';
import { SettingsGroup } from '../../components/ui/SettingsSurface';
import { CardSelectCheckbox } from '../../components/ui/CardSelectCheckbox';
import { SelectionActionBar } from '../../components/ui/SelectionActionBar';
import { Modal, ModalBody, ModalFooter } from '../../components/ui/Modal';
import type { PageFilterField } from 'elowen-plugin-ui-kit';
import { ControlSurfaceRegister, ControlSurfaceState, ControlSurfaceToolbar } from '../../components/ui/ControlSurface';
import { WorkspaceDetailRail } from '../../components/ui/WorkspacePrimitives';
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/states';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { OperationFailureReport } from '../../components/ui/OperationFailureReport';
import { useTranslation } from '../../lib/i18n';
import { ElowenApiError } from '../../lib/elowenClient';
import { rereadQuery } from '../../lib/rereadQuery';
import { holdsAnswer, releasedBy, releasing } from '../../lib/saveFeedback';
import { useMe } from '../../lib/queries';


import { NAME_RE } from '../../lib/nameGrammar';

/** One page of entries until the reader chooses another size — the same register step the rest of the
 *  app starts at. The choice is NOT persisted here, unlike the memory register: this component is a
 *  shared editor two different asset pages mount, and it has no identity of its own to key a stored
 *  preference on. Giving it one means a new public prop on a component that is part of the plugin ABI,
 *  which is a decision worth making on its own rather than as a side effect of adding a select. */
const DEFAULT_PAGE_SIZE = 25;

/** The page-level card of a register write that may have been applied. */
const HELD_KEY = 'asset-register-held';

type SourceFilter = 'all' | 'user' | 'builtin';

/** The shape every markdown asset (skill / sub-agent) shares. `source === 'user'` marks the
 *  editable, user-owned entries; anything else ships read-only. */
export interface MarkdownAsset {
  name: string;
  description: string;
  source: string;
  /** The account this entry belongs to, for assets that can be owned per user (per-user skills).
   *  `null`/absent = instance-wide. Two accounts may hold the same NAME, so rows are keyed and selected
   *  by name AND owner — keying on the name alone would make one row highlight (and delete) another. */
  owner?: number | null;
  /** Whether THIS caller may write it. The daemon decides (it owns the rule); absent means yes, so an
   *  asset type without per-user ownership behaves exactly as before. A row the caller cannot write
   *  renders read-only rather than offering controls whose request would be refused. */
  canDelete?: boolean;
}

/** Identity of a row. Not the name: with per-user assets the same name legitimately exists twice. */
const assetKey = (item: MarkdownAsset): string => `${item.source}:${item.owner ?? 'instance'}:${item.name}`;

/** Keeps a click on a control inside a card from also opening the entry the card stands for. */
const stopCardClick = (event: MouseEvent) => event.stopPropagation();

/** The glyph of each source. The source filter's options and the mark that leads an entry both read it,
 *  so a source keeps one glyph wherever it is named. */
const SOURCE_ICON: Record<Exclude<SourceFilter, 'all'>, LucideIcon> = { user: User, builtin: Package };

/** The editor's form: the fields both editors share plus the caller's divergent extra fields `E`
 *  (`body` is the primary markdown textarea — the skill's content or the sub-agent's prompt). */
export type AssetForm<E> = { editing: string | null; name: string; description: string; body: string } & E;

/** Success/error handlers the caller wires straight into its mutation's `mutate(..., callbacks)`. */
interface SaveCallbacks { onSuccess: () => void; onError: (error: unknown) => void }

/** What a per-item write returns. A promise (from `mutateAsync`) lets the bulk runner tell one item's
 *  outcome from another's: several `mutate` calls fired at one TanStack mutation detach every previous
 *  call's callbacks (`mutationObserver.mutate` overwrites `#mutateOptions` and removes itself from the
 *  previous in-flight mutation), so counting on callbacks alone stops at the last call. A caller that
 *  only `mutate`s returns nothing and the runner falls back to its callbacks. */
type SaveResult = void | Promise<unknown>;

export interface MarkdownAssetEditorProps<T extends MarkdownAsset, E> {
  query: UseQueryResult<T[]>;
  /** The page or section title (the hero's title on its own page). Names the card list and the pager
   *  for assistive technology, the way the MCP register is named by its title. Optional so bundles
   *  built before it keep their previous label; omit it only from those. */
  title?: string;
  labels: {
    empty: string;
    badgeUser: string;
    badgeBuiltin: string;
    remove: string;
    save: string;
    cancel: string;
    name: string;
    nameHint: string;
    namePlaceholder: string;
    description: string;
    descriptionHint: string;
    body: string;
    bodyHint: string;
    bodyPlaceholder?: string;
    deleteTitle: string;
    deleteDesc: string;
    /** Title of the detail drawer when a new entry is being written. */
    addTitle: string;
  };
  /** The blank form used when adding a new entry. */
  emptyForm: AssetForm<E>;
  /** Map an existing entry to the form when its row is opened. */
  formFromItem: (item: T) => AssetForm<E>;
  /** Extra validation on top of name/description/body being non-empty (defaults to always valid). */
  extraValid?: (form: AssetForm<E>) => boolean;
  /** Read-only badges rendered after the source badge (e.g. tools mode, manual-only). */
  renderBadges?: (item: T) => ReactNode;
  /** Extra per-row control rendered in the register's LEADING column. The caller owns whether a read-only
   * entry receives an enabled control, a disabled status switch or no control at all. */
  renderRowControl?: (item: T) => ReactNode;
  /** Ownership column + scope filter, for assets that can belong to one account. Omitted → the register
   *  looks exactly as it did before ownership existed (no extra column, one filter). */
  ownership?: {
    header: string;
    label: (item: T) => string;
    /** The glyph that names the scope inside the filter's option list, the way every filter in the app
     *  does. A caller supplies the icon it already uses for that scope elsewhere, so one concept keeps one
     *  glyph. Omitted, the option simply shows no glyph. */
    scopes: { value: string; label: string; icon?: LucideIcon; matches: (item: T) => boolean }[];
  };
  /** One named column of the caller's own, between the description and the source. It is for a fact that
   *  belongs to every row and reads as a value, not as a mark — the model a sub-agent runs on, say. Such a
   *  fact squeezed in beside the source badge shares that column's 16rem with it and clips, which is how a
   *  register ends up showing a bare ellipsis instead of an answer. `ownership` is the variant that also
   *  filters; this one only names. Omitted → the register has exactly the columns it had before. */
  column?: { header: string; render: (item: T) => ReactNode };
  /** Filter fields the page contributes to the register's ONE condensed filter control, after the scope
   *  (or source) field this component always owns. A page that narrows the same list on an axis of its
   *  own — which account an admin is reading, say — hands that field over here instead of growing a
   *  second dropdown or a block above the list, so the trigger, its count and the dismissible chips stay
   *  one thing. The control inside is the page's; the register never reads its value, only the page's
   *  own `active`/`activeLabel`/`onReset`. Omitted or empty → the toolbar is exactly what it was. */
  extraFilters?: PageFilterField[];
  /** Detail rail for a row this caller may NOT edit but which still carries a setting of its own — a
   *  sub-agent's per-account model belongs beside the agent it applies to, not in a second panel stacked
   *  above the register listing the same names again. `has` decides which read-only rows open at all, so
   *  an asset type without such a setting (skills) keeps today's behaviour: a read-only row has no
   *  chevron and does not open. The rail's body is the caller's; the register only frames it, in the same
   *  drawer an editable row opens. */
  inspect?: {
    has: (item: T) => boolean;
    render: (item: T) => ReactNode;
    /** The `owner` of a page-owned writer (a `PluginConfigDraft`) that the rail's body edits: the rail hosts that
     *  writer's saved and recovery cards while it is open. */
    reportedBy?: string;
  };
  /** Extra form fields rendered between the name/description grid and the body textarea. */
  renderFieldsBeforeBody?: (form: AssetForm<E>, patch: (p: Partial<AssetForm<E>>) => void) => ReactNode;
  /** Extra form fields rendered after the body textarea. */
  renderFieldsAfterBody?: (form: AssetForm<E>, patch: (p: Partial<AssetForm<E>>) => void) => ReactNode;
  /** Persist the form (create or update); wire the callbacks into the caller's mutation. */
  onSave: (form: AssetForm<E>, callbacks: SaveCallbacks) => void;
  /** True while a create/update is in flight (disables the save button). */
  saving: boolean;
  /** Delete a user entry; the whole item is passed because a name alone no longer identifies one.
   *  Returning the write's promise (from `mutateAsync`) lets a bulk run tell this item's outcome from
   *  the next one's — see `SaveResult`. */
  onDelete: (item: T, callbacks: SaveCallbacks) => SaveResult;
  /** Per-item enable switch behind the register's bulk enable/disable actions. When provided,
   *  selectable entries gain those two actions running through this write one item at a time; omitted,
   *  the floating bar offers delete only. A caller whose rows carry an enable switch in
   *  `renderRowControl` wires the same mutation here, returning its promise. */
  onSetEnabled?: (item: T, enabled: boolean, callbacks: SaveCallbacks) => SaveResult;
  /** The page's hero owns the primary "add" action, so the button lives outside this component and
   *  drives the create drawer through this pair. */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
  /** The add button, passed in only by a surface that has no hero to carry it (the Settings deck), so
   *  the page never shows the same action twice. */
  addAction?: ReactNode;
}

/** Shared register for the markdown-asset pages (skills + sub-agents): the workspace toolbar (search +
 *  source filter), the entries as full-width cards, one page at a time, and the create/edit form in the
 *  workspace detail drawer. It composes the same primitives the built-in workspaces use, so a plugin
 *  page is not recognisable as "the plugin one" by its chrome.
 *
 *  Entries the reader may act on (user-owned, editable) carry a select checkbox and raise the shared
 *  floating bulk bar: enable/disable selected when `onSetEnabled` is provided, delete selected behind
 *  a confirm, all fanned out per item through the caller's own writes. Built-in entries carry no
 *  checkbox.
 *
 *  The divergent bits — extra form fields, per-row controls, badges and the save strategy — are
 *  injected by the caller. Built-in entries ship read-only: their rows carry no controls and do not
 *  open. */
export function MarkdownAssetEditor<T extends MarkdownAsset, E>({
  query, title, labels, emptyForm, formFromItem, extraValid, renderBadges, renderRowControl, ownership, column, inspect,
  extraFilters, renderFieldsBeforeBody, renderFieldsAfterBody, onSave, saving, onDelete, onSetEnabled, creating,
  onCreatingChange, addAction,
}: MarkdownAssetEditorProps<T, E>) {
  const { t } = useTranslation();
  const reportFailure = useOperationFailure();
  const { dismissKey } = useToast();
  const me = useMe();
  /** The person an account-owned entry belongs to, as the avatar chip draws them: the signed-in account
   *  itself when it is the owner (so the chip carries their picture, not the letters of a "Mine" label),
   *  else the owner's id under the label the caller gives them. */
  const ownerChip = (owner: number, label: string) =>
    me.data?.user.id === owner ? me.data.user : { id: owner, username: label };
  const [form, setForm] = useState<AssetForm<E> | null>(null);
  // The item behind the open form / the pending delete, not just its name — see `assetKey`.
  const [editing, setEditing] = useState<T | null>(null);
  // The row open in the read-only rail. Separate from `editing` because the two rails are different
  // things: one edits a definition, the other inspects one and adjusts what belongs to the reader.
  const [inspecting, setInspecting] = useState<T | null>(null);
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);
  /** Bulk selection, keyed by `assetKey` (name AND owner — a name alone does not identify a row). */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  /** A bulk run in flight. While set, the checkboxes and the bar lock so the processed set cannot
   *  change underneath the run. */
  const [bulkBusy, setBulkBusy] = useState(false);
  /** The failure of the open form's save, reported from inside the form's own surface. */
  const [saveFailure, setSaveFailure] = useState<{ error: unknown } | null>(null);
  /** The daemon's validation sentence for the form's values (a 400), kept with the form until it is edited. */
  const [saveProblem, setSaveProblem] = useState<string | null>(null);
  /** A write of this register (a save, a delete, a bulk run) whose outcome is unknown or lost to a newer state. It
   *  may have been applied, so every write waits (Save, the bulk bar, the delete confirmations) until the register
   *  was read again; that re-read is the card's recovery. */
  const [held, setHeld] = useState<{ error: unknown } | null>(null);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<SourceFilter>('all');
  const [scope, setScope] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Back to the first page: growing the window would otherwise land the reader on a page they never
  // asked for, and shrinking it can leave the page past the end of the register.
  const changePageSize = (next: number) => { setPageSize(next); setPage(0); };

  // The hero's add button only flips a flag; the blank form is this component's to own, and an edit
  // already in the drawer wins — the flag must not wipe what the user is typing.
  useEffect(() => { if (creating) setForm((cur) => cur ?? emptyForm); }, [creating, emptyForm]);
  // A narrowed list can be shorter than the page the user is on; landing on an empty page reads as
  // "nothing matches" when the matches are simply on page 1.
  useEffect(() => { setPage(0); }, [search, source, scope]);

  const { data, isLoading, isError } = query;
  const items = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const scopeRule = ownership?.scopes.find((sc) => sc.value === scope);
    return items.filter((item) => {
      if (source === 'user' && item.source !== 'user') return false;
      if (source === 'builtin' && item.source === 'user') return false;
      if (scopeRule && !scopeRule.matches(item)) return false;
      if (needle === '') return true;
      return item.name.toLowerCase().includes(needle) || item.description.toLowerCase().includes(needle);
    });
  }, [items, search, source, scope, ownership]);
  const { page: clampedPage, items: pageItems } = useMemo(() => paginateItems(filtered, page, pageSize), [filtered, page, pageSize]);
  // The existing scope rules remain authoritative. Scopes need not cover the whole catalogue:
  // entries outside them keep their source group rather than disappearing.
  const scopeGroups = ownership?.scopes.map((rule) => ({
    key: `scope:${rule.value}`, title: rule.label, icon: rule.icon,
    items: pageItems.filter(rule.matches),
  })) ?? [];
  const unscoped = pageItems.filter((item) => !ownership?.scopes.some((rule) => rule.matches(item)));
  const groups = [
    ...scopeGroups,
    { key: 'source:user', title: t.assetEditor.filterUser, icon: User,
      items: unscoped.filter((item) => item.source === 'user') },
    { key: 'source:builtin', title: t.assetEditor.filterBuiltin, icon: Package,
      items: unscoped.filter((item) => item.source !== 'user') },
  ].filter((group) => group.items.length > 0);
  const revealMatches = search.trim() !== '' || source !== 'all' || scope !== 'all';

  const toggleSelect = (key: string) => {
    if (bulkBusy) return;
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const clearSelection = () => {
    if (bulkBusy) return;
    setSelected(new Set());
  };

  // Keep the bulk selection consistent with what is on screen: drop a selected key the
  // filter/search no longer shows, so the bar never counts entries outside the visible set.
  useEffect(() => {
    const visible = new Set(filtered.map(assetKey));
    setSelected((cur) => {
      if (cur.size === 0) return cur;
      const next = new Set<string>();
      for (const key of cur) if (visible.has(key)) next.add(key);
      return next.size === cur.size ? cur : next;
    });
  }, [filtered]);

  const patch = (p: Partial<AssetForm<E>>) => setForm((cur) => (cur ? { ...cur, ...p } : cur));
  const closeForm = () => { setForm(null); setEditing(null); onCreatingChange(false); };
  // Opening either rail closes the other: two drawers over one register is never what a click meant.
  // The create form is closed through `closeForm` so the caller's `creating` flag is released too; a flag left
  // set would turn the next click on Add into a no-op.
  const openInspect = (item: T) => { closeForm(); setInspecting(item); };
  const nameValid = form !== null && NAME_RE.test(form.name.trim());
  const savable = form !== null && (form.editing !== null || nameValid)
    && form.description.trim() !== '' && form.body.trim() !== '' && (extraValid?.(form) ?? true);

  /** The authoritative re-read every recovery of this register ends in. It adopts what it read into whatever the
   *  register has open, and only then ends the hold: an open edit takes the canonical record (the draft is
   *  discarded, as for any reload), a create whose record exists is complete and closes, a confirmation about
   *  something that is gone closes. */
  const release = useCallback(() => setHeld(null), []);
  const adoptCanonical = (fresh: T[]) => {
    if (form && editing) {
      const current = fresh.find((item) => assetKey(item) === assetKey(editing));
      if (current) { setEditing(current); setForm(formFromItem(current)); } else closeForm();
    } else if (form && fresh.some((item) => item.source === 'user' && item.name === form.name.trim())) {
      closeForm();
    }
    if (pendingDelete && !fresh.some((item) => assetKey(item) === assetKey(pendingDelete))) setPendingDelete(null);
    if (confirmBulkDelete && !fresh.some((item) => selected.has(assetKey(item)))) setConfirmBulkDelete(false);
  };
  const reloadRegister = releasing(async () => { adoptCanonical(await rereadQuery(query.refetch)); }, release);
  const hold = (error: unknown) => { if (holdsAnswer(error)) setHeld({ error: releasedBy(error, release) }); };
  // ONE hold, projected into the surface that is active: the form raises its card inside itself while it is open
  // (below), a confirmation reports through its own rejection, and with none open the page raises it here. A
  // surface opening or closing moves the card; it is never duplicated and never left without its recovery.
  const surfaceOpen = form !== null || pendingDelete !== null || confirmBulkDelete;
  const heldRecovery = useRef({ message: t.common.actionFailed, reload: reloadRegister });
  heldRecovery.current = { message: t.common.actionFailed, reload: reloadRegister };
  useEffect(() => {
    if (!held || surfaceOpen) return;
    reportFailure(HELD_KEY, held.error, { message: heldRecovery.current.message, reload: () => heldRecovery.current.reload() });
    return () => dismissKey(HELD_KEY);
  }, [held, surfaceOpen, reportFailure, dismissKey]);

  // The validation sentence belongs to the values it was about: any edit (or closing the form) ends it.
  useEffect(() => { setSaveProblem(null); setSaveFailure(null); }, [form]);

  const submit = () => {
    if (!form || !savable || held) return;
    setSaveFailure(null);
    setSaveProblem(null);
    onSave(form, {
      onSuccess: () => { closeForm(); },
      onError: (e) => {
        // A 400 is the definition's own validation: it names a value of this form, so it stays with the form.
        // Anything else is the save command failing; a write that may have been applied also holds the register.
        if (e instanceof ElowenApiError && e.status === 400 && e.code) { setSaveProblem(e.code); return; }
        if (holdsAnswer(e)) hold(e);
        else setSaveFailure({ error: e });
      },
    });
  };

  /** The confirmation's own promise: a refused delete rejects, the dialog reports it and stays open. */
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const outcome = await runBulkItem(pendingDelete, (item, callbacks) => onDelete(item, callbacks));
    if (!outcome.ok) { hold(outcome.error); throw outcome.error; }
    // The open drawer belongs to the row that has gone away.
    if (editing && assetKey(editing) === assetKey(pendingDelete)) closeForm();
    setPendingDelete(null);
  };

  // Bulk writes have no bulk endpoint, so they run through the caller's own per-item path one item
  // at a time. Sequentially, never fanned out: several `mutate` calls fired at one TanStack mutation
  // detach every previous call's callbacks, and one shared optimistic snapshot cannot roll back per
  // item — so each item's outcome is awaited before the next starts. A returned promise decides the
  // item; without one its callbacks do. Only keys whose own write succeeded leave the selection;
  // failures stay picked for a retry and are reported once, in the app's words (delete through its confirmation,
  // enable/disable as a card of its own).
  const bulkTargets = () => filtered.filter((item) => selected.has(assetKey(item)));
  type BulkOutcome = { key: string; ok: boolean; error?: unknown };
  const runBulkItem = (
    item: T,
    act: (item: T, callbacks: SaveCallbacks) => SaveResult,
  ): Promise<BulkOutcome> => new Promise((resolve) => {
    const key = assetKey(item);
    let settled = false;
    const settle = (ok: boolean, error?: unknown) => {
      if (!settled) { settled = true; resolve({ key, ok, error }); }
    };
    try {
      const returned = act(item, { onSuccess: () => settle(true), onError: (e) => settle(false, e) });
      if (returned && typeof (returned as Promise<unknown>).then === 'function') {
        (returned as Promise<unknown>).then(() => settle(true), (e) => settle(false, e));
      }
    } catch (e) { settle(false, e); }
  });
  /** Drops the keys whose own write succeeded from the selection; a failed key stays picked for a retry. */
  const finishBulk = (results: BulkOutcome[]) => {
    const okKeys = new Set(results.filter((r) => r.ok).map((r) => r.key));
    setSelected((cur) => {
      if (cur.size === 0) return cur;
      const next = new Set(cur);
      for (const key of okKeys) next.delete(key);
      return next;
    });
  };
  /** Runs the writes one after another and stops at the first one whose outcome is unknown (or lost to a newer
   *  state): the next write would go out while that one is still unverified. Items an ordinary refusal rejected do
   *  not stop the run. */
  const runBulk = async (targets: T[], act: (item: T, callbacks: SaveCallbacks) => SaveResult): Promise<BulkOutcome[]> => {
    const results: BulkOutcome[] = [];
    for (const item of targets) {
      const outcome = await runBulkItem(item, act);
      results.push(outcome);
      if (!outcome.ok && holdsAnswer(outcome.error)) break;
    }
    return results;
  };
  /** Several items can fail in one run, but the person did one thing: one answer. The failure that decides it is the
   *  one that forbids answering again (an unknown outcome), else the first refusal; its error is the one classified. */
  const decisiveFailure = (results: BulkOutcome[]): BulkOutcome | undefined =>
    results.find((r) => !r.ok && holdsAnswer(r.error)) ?? results.find((r) => !r.ok);
  const bulkDelete = async () => {
    const targets = bulkTargets();
    if (bulkBusy || held || targets.length === 0) { setConfirmBulkDelete(false); return; }
    setBulkBusy(true);
    let failure: BulkOutcome | undefined;
    try {
      // The open drawer belongs to rows that are going away — decided up front from the picked set,
      // the same set the run processes.
      if (editing && selected.has(assetKey(editing))) closeForm();
      const results = await runBulk(targets, (it, cb) => onDelete(it, cb));
      finishBulk(results);
      failure = decisiveFailure(results);
    } finally {
      setBulkBusy(false);
    }
    // A run with a refusal is a refused confirmation: the dialog reports it once and stays open over what is still
    // picked, instead of closing as if it had succeeded. An unknown outcome also holds the dialog (see ConfirmDialog).
    if (failure) { hold(failure.error); throw failure.error; }
    setConfirmBulkDelete(false);
  };
  const bulkSetEnabled = async (enabled: boolean) => {
    if (!onSetEnabled) return;
    const targets = bulkTargets();
    if (bulkBusy || held || targets.length === 0) return;
    setBulkBusy(true);
    try {
      const setEnabled = onSetEnabled;
      const results = await runBulk(targets, (it, cb) => setEnabled(it, enabled, cb));
      finishBulk(results);
      const failure = decisiveFailure(results);
      // A write that may have been applied holds the register, whose card (with the re-read) the page raises; an
      // ordinary refusal is one card of its own and holds nothing.
      if (failure && holdsAnswer(failure.error)) hold(failure.error);
      else if (failure) reportFailure('asset-bulk-enable', failure.error, { message: t.common.actionFailed, reload: reloadRegister });
    } finally { setBulkBusy(false); }
  };

  // ONE filter, never two: an asset type with ownership scopes already splits the same set more finely
  // (mine / instance / bundled), so offering the coarse source filter beside it would give two controls
  // whose answers overlap — and "Built-in" in both of them.
  const scopeLabel = ownership?.scopes.find((sc) => sc.value === scope)?.label ?? '';
  const sourceLabel = source === 'user' ? t.assetEditor.filterUser : t.assetEditor.filterBuiltin;
  const filterName = ownership ? ownership.header : t.assetEditor.colSource;
  // One `SelectMenu` per axis with its neutral option first and a glyph on every entry, exactly as the
  // Sites register does: a bare label makes two options of the same length read as one column.
  const filterOptions = ownership
    ? [
      { value: 'all', label: t.assetEditor.filterAll, icon: <Layers size={14} /> },
      ...ownership.scopes.map((sc) => {
        const Icon = sc.icon;
        return { value: sc.value, label: sc.label, ...(Icon ? { icon: <Icon size={14} /> } : {}) };
      }),
    ]
    : [
      { value: 'all', label: t.assetEditor.filterAll, icon: <Layers size={14} /> },
      { value: 'user', label: t.assetEditor.filterUser, icon: <SOURCE_ICON.user size={14} /> },
      { value: 'builtin', label: t.assetEditor.filterBuiltin, icon: <SOURCE_ICON.builtin size={14} /> },
    ];
  const filterControl = ownership ? (
    <SelectMenu
      value={scope}
      onChange={setScope}
      options={filterOptions}
      label={ownership.header}
    />
  ) : (
    <SelectMenu
      value={source}
      onChange={(value) => setSource(value as SourceFilter)}
      options={filterOptions}
      label={t.assetEditor.colSource}
    />
  );
  const filterActive = ownership ? scope !== 'all' : source !== 'all';
  const filterField: PageFilterField = filterActive
    ? {
      id: 'asset-filter',
      label: filterName,
      control: filterControl,
      active: true,
      activeLabel: `${filterName}: ${ownership ? scopeLabel : sourceLabel}`,
      onReset: () => { if (ownership) setScope('all'); else setSource('all'); },
    }
    : { id: 'asset-filter', label: filterName, control: filterControl, active: false };
  // The page's contributed fields ride in the SAME control as this one: one trigger, one count, one chip
  // row, so a filter a page adds cannot be listed by the panel and missed by the chips, or counted twice.
  const filterFields = extraFilters?.length ? [filterField, ...extraFilters] : [filterField];
  // Search and the add action stay VISIBLE in the page's toolbar row while every narrowing control folds
  // behind the shared filter surface, and the row stays on screen whatever the body beneath it is doing,
  // as it does in every other register in the app. It is how the reader CHANGES what is being looked at:
  // hiding it while a load is in flight removes the control that ends it. That row is the canonical page
  // toolbar, contributed through the structured props rather than rebuilt here, so its order, its chips
  // line and its narrow fold are the seam's and cannot drift from the pages beside it. The hand-rolled
  // row this replaces had to defend its own width against `.control-surface-toolbar`'s unlayered
  // `align-items: center` and still ran 42px past the surface at 320px.
  const toolbar = (
    <ControlSurfaceToolbar
      search={<RegisterSearch value={search} onChange={setSearch} placeholder={t.assetEditor.search} label={t.assetEditor.search} />}
      filters={filterFields}
      actions={addAction}
    />
  );
  // The register's state line, drawn INSTEAD of the register body while the toolbar above it stays put.
  const registerState = isError
    ? <ControlSurfaceState tone="danger"><ErrorState message={t.common.daemonUnreachable} onRetry={() => query.refetch()} /></ControlSurfaceState>
    : isLoading || !data
      ? <ControlSurfaceState><LoadingState variant="cards" /></ControlSurfaceState>
      : null;
  /** What the card list and the pager are named: the page's own title, falling back to the generic
   *  column name for callers built before the title travelled with the component. */
  const listLabel = title ?? t.assetEditor.colName;
  // The entry's mark: the glyph of the scope it belongs to, the same one the filter shows for that scope, or
  // of its source when the caller declares no scopes. The row card and the drawers' header tile draw the same.
  const markOf = (item: T): LucideIcon => ownership?.scopes.find((sc) => sc.matches(item))?.icon ?? SOURCE_ICON[item.source === 'user' ? 'user' : 'builtin'];

  return (
    <>
    <div className="flex min-w-0 flex-col gap-4">
      {toolbar}

      {/* Nothing here returns early: an open form belongs to the reader, not to the request in flight, so
          loading a list must not unmount the drawer written over it. */}
      {registerState ?? <ControlSurfaceRegister className="flex flex-col gap-4">
        {items.length === 0 ? <EmptyState title={labels.empty} />
          : filtered.length === 0 ? <EmptyState title={t.assetEditor.emptySearch} icon={Search} />
          : (
            <div className="flex min-w-0 flex-col gap-3">
              {/* One card per row, not a table: an entry is a name, a sentence saying when to use it, where
                  it came from and who owns it, and a table could only preview that sentence in one clipped
                  line. A full-width row gives the sentence the whole width and every marker room to wrap. */}
              {groups.map((group) => (
                <SettingsGroup key={group.key} title={group.title} icon={group.icon}
                  className="border-0 bg-transparent py-0 shadow-none sm:py-0 [&>[data-slot=card-header]]:px-0 [&_[data-slot=card-content]]:px-0"
                  collapsible storageKey={`markdown-assets:${group.key}`} forceOpen={revealMatches}>
              <CardGrid layout="list" aria-label={`${listLabel}: ${group.title}`}>
                {group.items.map((item) => {
                  // Two different questions, deliberately kept apart: WHERE the entry came from (drives the
                  // badge and the source filter, so the same row reads the same way for everyone) and
                  // whether THIS caller may write it (drives the controls). An instance-wide skill shown to
                  // somebody who may not edit it is still a custom skill, not a built-in one.
                  const isUser = item.source === 'user';
                  const editable = isUser && item.canDelete !== false;
                  // A row nobody may edit can still be worth opening when the caller has something to
                  // show or set on it; without that it stays closed, as it always has.
                  const inspectable = !editable && inspect?.has(item) === true;
                  const open = editable
                    ? () => { setInspecting(null); setForm(formFromItem(item)); setEditing(item); }
                    : () => openInspect(item);
                  const openedRow = editing ?? inspecting;
                  const key = assetKey(item);
                  const isOpen = openedRow !== null && assetKey(openedRow) === key;
                  const opens = editable || inspectable;
                  // Only an entry the reader may act on is selectable: a built-in entry carries no
                  // checkbox and never joins the bulk selection.
                  const selectable = editable;
                  const bulkSelected = selected.has(key);
                  // The checkbox names the scope beside the name: the same name can exist twice under
                  // different owners, and a bare name would make the two ticks indistinguishable.
                  const selectScope = ownership ? ownership.label(item) : isUser ? labels.badgeUser : labels.badgeBuiltin;
                  const Mark = markOf(item);
                  return (
                    <CardGridItem
                      key={key}
                      data-asset-card={key}
                      selected={isOpen}
                      bulkSelected={bulkSelected}
                      // The surface opens the entry for the pointer; the name below is the one real open
                      // control, and the card's keyboard stop. A card nothing opens stays a quiet surface.
                      onClick={opens ? open : undefined}
                    >
                      {/* `items-center`: the row's controls are of very different heights — a 36px delete
                          button, a 36px mark tile, and an 18px Toggle that must STAY 18px (owner's rule: no
                          36px box around a switch). Centring the row is what puts all three on one line, so
                          no control has to carry a height to take part, and the Toggle needs no wrapper
                          floor of its own. */}
                      <div className="flex min-w-0 items-center gap-3">
                        {/* The select checkbox leads the card, ahead of the caller's own control. Ticking
                            it never opens the entry underneath. */}
                        {selectable ? (
                          <span className="flex h-9 shrink-0 items-center" onClick={stopCardClick}>
                            <CardSelectCheckbox
                              checked={bulkSelected}
                              label={t.assetEditor.selectRow.replace('{name}', item.name).replace('{scope}', selectScope)}
                              onToggle={() => toggleSelect(key)}
                              disabled={bulkBusy}
                            />
                          </span>
                        ) : null}
                        {/* The caller's control and the delete action are controls of their own: a click on
                            them must not also open the entry underneath. The wrapper only CENTRES what the
                            caller returns — in both real consumers that is a Toggle, which is deliberately
                            small (an 18px track, no 36px box), so this span states no height and no box of
                            its own and a lone switch is not padded out to a control's step. */}
                        {renderRowControl ? (
                          <span className="flex shrink-0 items-center" onClick={stopCardClick}>{renderRowControl(item)}</span>
                        ) : null}
                        {/* Decorative: the badge beside the name already says the same in words. Tinted for
                            an entry of the reader's own making, quiet for one that shipped. */}
                        <IconTile aria-hidden data-asset-mark tone={isUser ? 'accent' : 'muted'}>
                          <Mark size={16} />
                        </IconTile>
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          {/* The identity line: the name, where it came from and the caller's markers, then the
                              owner and the caller's fact pushed to the right edge. On a narrow row the facts
                              wrap under the name instead of squeezing the description beneath. */}
                          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            {/* Level 2: the page title is the hero's `h1` and nothing sits between it and a card.
                                The name wraps rather than truncates: it is the entry's identity. */}
                            <h2 className="min-w-0 break-words font-mono text-card-title text-foreground" title={item.name}>
                              {opens ? (
                                // The label is the host dictionary's short name, not `labels`: a new required
                                // entry there would be a contract change every plugin embedding this register
                                // would have to ship before its entries could open again.
                                <TitleLink
                                  aria-label={t.assetEditor.openRow.replace('{name}', item.name)}
                                  onClick={(event) => { event.stopPropagation(); open(); }}
                                  className="text-card-title text-foreground group-hover:text-primary"
                                >
                                  {item.name}
                                </TitleLink>
                              ) : item.name}
                            </h2>
                            {/* Markers wrap rather than clip: a clipped badge is read out by assistive
                                technology while invisible on screen. */}
                            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                              <LabelMark tone={isUser ? 'accent' : 'default'}>{isUser ? labels.badgeUser : labels.badgeBuiltin}</LabelMark>
                              {renderBadges?.(item)}
                            </span>
                            {ownership || column ? (
                              <span className="ml-auto flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                                {ownership ? (
                                  <span className="flex min-w-0 items-center gap-1.5 text-small text-muted-foreground" title={`${ownership.header}: ${ownership.label(item)}`}>
                                    <span className="shrink-0 text-muted-foreground/70">{ownership.header}</span>
                                    {/* An entry owned by one account carries that person's chip, the same one
                                        project teams show; the name beside it is what is read out. */}
                                    {item.owner != null ? (
                                      <span aria-hidden className="flex shrink-0">
                                        <Avatar user={ownerChip(item.owner, ownership.label(item))} size={16} />
                                      </span>
                                    ) : null}
                                    <span className="min-w-0 truncate">{ownership.label(item)}</span>
                                  </span>
                                ) : null}
                                {column ? (
                                  <span className="flex min-w-0 items-center gap-1.5 text-small text-muted-foreground">
                                    <span className="shrink-0 text-muted-foreground/70">{column.header}</span>
                                    {column.render(item)}
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                          </div>
                          {/* When to use it: the sentence the row exists to show, across the whole width. */}
                          <p className="line-clamp-2 text-meta text-muted-foreground" title={item.description || undefined}>
                            {item.description || '—'}
                          </p>
                        </div>
                        {editable ? (
                          <span className="shrink-0" onClick={stopCardClick}>
                            <Button variant="ghost-danger" icon={Trash2} aria-label={labels.remove} disabled={held !== null} onClick={() => setPendingDelete(item)} />
                          </span>
                        ) : null}
                      </div>
                    </CardGridItem>
                  );
                })}
              </CardGrid>
                </SettingsGroup>
              ))}

              <Pager
                page={clampedPage}
                pageSize={pageSize}
                total={filtered.length}
                onPageChange={setPage}
                onPageSizeChange={changePageSize}
                ariaLabel={listLabel}
              />
            </div>
          )}
      </ControlSurfaceRegister>}

      {/* The shared dialog's own body and footer: the actions used to be the last child of the scroll
          region behind a hand-drawn top rule, which is the divider `ModalFooter` already paints — and it
          scrolled away with the form it belonged to. The shared Modal follows viewport and depth;
          the rail's own width keeps the presentation the register had. */}
      {form ? (
        <Modal
          title={form.editing !== null ? form.editing : labels.addTitle}
          icon={editing !== null ? markOf(editing) : SOURCE_ICON.user}
          description={form.editing !== null ? form.description || undefined : undefined}
          closeLabel={t.common.close}
          size="md"
          drawerWidth="default"
          onClose={saving ? () => {} : closeForm}
          closeDisabled={saving}
        >
          <ModalBody gap={4}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={labels.name} hint={labels.nameHint}>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((cur) => (cur ? { ...cur, name: e.target.value } : cur))}
                  disabled={form.editing !== null}
                  className={`font-mono ${form.editing === null && form.name !== '' && !nameValid ? 'border-destructive' : ''}`}
                  placeholder={labels.namePlaceholder}
                />
              </Field>
              <Field label={labels.description} hint={labels.descriptionHint}>
                <Input value={form.description} onChange={(e) => setForm((cur) => (cur ? { ...cur, description: e.target.value } : cur))} />
              </Field>
            </div>
            {renderFieldsBeforeBody?.(form, patch)}
            <Field label={labels.body} hint={labels.bodyHint}>
              <Textarea value={form.body} onChange={(e) => setForm((cur) => (cur ? { ...cur, body: e.target.value } : cur))} rows={14} placeholder={labels.bodyPlaceholder} />
            </Field>
            {renderFieldsAfterBody?.(form, patch)}
            {saveProblem ? <p role="alert" className="text-ui text-destructive">{saveProblem}</p> : null}
          </ModalBody>
          <ModalFooter
            status={editing !== null ? (
              <Button variant="ghost-danger" icon={Trash2} disabled={held !== null} onClick={() => setPendingDelete(editing)}>{labels.remove}</Button>
            ) : undefined}
          >
            <Button variant="outline" onClick={closeForm}>{labels.cancel}</Button>
            <Button variant="accent" onClick={submit} disabled={!savable || saving || held !== null}>{labels.save}</Button>
          </ModalFooter>
          <OperationFailureReport reportKey="asset-form-failed" failure={held ?? saveFailure} message={t.common.actionFailed} reload={reloadRegister} />
        </Modal>
      ) : null}

      {/* The read-only rail: the SAME detail drawer an editable row opens, so a reader who may only look
          at an entry still meets it in the place the register always puts a record. Its header already
          carries the name and the one-line description, so the caller's body starts at what it came for. */}
      {inspecting && inspect ? (
        <WorkspaceDetailRail
          label={inspecting.name}
          icon={markOf(inspecting)}
          description={inspecting.description || undefined}
          closeLabel={t.common.close}
          reportedBy={inspect.reportedBy}
          onClose={() => setInspecting(null)}
        >
          {inspect.render(inspecting)}
        </WorkspaceDetailRail>
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={labels.deleteTitle}
        description={pendingDelete ? labels.deleteDesc.replace('{name}', pendingDelete.name) : undefined}
        confirmLabel={labels.remove}
        confirmDisabled={held !== null}
        reload={reloadRegister}
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>

      {/* Floating bulk toolbar, kept a sibling of the layout so it is never clipped. Delete stays
          behind the same explicit confirmation as the per-item delete, with the count in the body. */}
      {selected.size > 0 ? (
        <SelectionActionBar
          count={t.assetEditor.selectedCount.replace('{n}', String(selected.size))}
          clearLabel={t.assetEditor.clearSelection}
          onClear={clearSelection}
          disabled={bulkBusy}
        >
          {onSetEnabled ? (
            <>
              <Button variant="default" icon={Power} disabled={bulkBusy || held !== null} onClick={() => void bulkSetEnabled(true)}>{t.assetEditor.bulkEnable}</Button>
              <Button variant="default" icon={PowerOff} disabled={bulkBusy || held !== null} onClick={() => void bulkSetEnabled(false)}>{t.assetEditor.bulkDisable}</Button>
            </>
          ) : null}
          <Button variant="default" icon={Trash2} disabled={bulkBusy || held !== null} onClick={() => setConfirmBulkDelete(true)}>{t.assetEditor.bulkDelete}</Button>
        </SelectionActionBar>
      ) : null}

      <ConfirmDialog
        open={confirmBulkDelete}
        title={t.assetEditor.bulkDeleteTitle}
        description={t.assetEditor.bulkDeleteBody.replace('{n}', String(selected.size))}
        confirmLabel={t.assetEditor.bulkDelete}
        confirmDisabled={held !== null}
        reload={reloadRegister}
        onConfirm={bulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
      />
    </>
  );
}
